// inventoryService.js
// API service for inventory management

import { HTTPService } from './httpService';

const API = new HTTPService();

// ============================================================================
// Units of Measure
// ============================================================================

export const getUnitsOfMeasure = async () => {
    const { data } = await API.get('/api/units');
    return data.data || data;
};

// ============================================================================
// Products
// ============================================================================

export const getProducts = async (installerId, categoryId = null) => {
    const params = { installerId };
    if (categoryId) {
        params.categoryId = categoryId;
    }
    const { data } = await API.get('/api/products', params);
    return data.data || data;
};

export const createProduct = async (productData) => {
    const { data } = await API.post('/api/products', productData);
    return data;
};

export const updateProduct = async (productId, productData) => {
    const { data } = await API.put(`/api/products/${productId}`, productData);
    return data;
};

export const deleteProduct = async (productId, installerId) => {
    const { data } = await API.delete(`/api/products/${productId}`, { params: { installerId } });
    return data;
};

// ============================================================================
// Inventory Stock
// ============================================================================

/**
 * Get stock levels for products
 * @param {number} installerId - Required
 * @param {object} options - Optional filters: categoryId, lowStockOnly
 */
export const getStockLevels = async (installerId, options = {}) => {
    const params = { installerId, ...options };
    const { data } = await API.get('/api/inventory/stock', params);
    return data.data || data;
};

/**
 * Get products below reorder point
 * @param {number} installerId - Required
 */
export const getLowStockAlerts = async (installerId) => {
    const { data } = await API.get('/api/inventory/low-stock', { installerId });
    return data.data || data;
};

// ============================================================================
// Inventory Items (rolls/units)
// ============================================================================

/**
 * Get individual inventory items for a product
 * @param {number} productId - Required
 * @param {number} installerId - Required - filters items to this installer only
 * @param {string} status - Optional: in_stock, in_use, depleted
 */
export const getInventoryItems = async (productId, installerId, status) => {
    const params = { productId, installerId };
    if (status) params.status = status;
    const { data } = await API.get('/api/inventory/items', params);
    return data.data || data;
};

/**
 * Lookup item by barcode
 * @param {string} barcode - The barcode to lookup
 * @param {number} installerId - Required - installer ID to scope the search
 * @returns {Object} { found, multiple, items, data } - full response for handling multiple products
 */
export const lookupBarcode = async (barcode, installerId) => {
    if (!installerId) {
        console.warn('[inventoryService] lookupBarcode called without installerId - this may return cross-installer data');
    }
    const url = `/api/inventory/barcode/${encodeURIComponent(barcode)}`;
    const params = { installerId };
    console.log('[inventoryService] lookupBarcode calling:', url, 'params:', params);
    try {
        const response = await API.get(url, params);
        console.log('[inventoryService] lookupBarcode response:', response);
        // Return full response to access found, multiple, items, and data fields
        // API format: { success, found, multiple, items: [...], data: {...} }
        return response.data;
    } catch (error) {
        console.error('[inventoryService] lookupBarcode error:', error);
        throw error;
    }
};

// ============================================================================
// Inventory Transactions
// ============================================================================

/**
 * Receive new inventory item
 * @param {object} itemData - { productId, installerId, barcode, quantity, lotNumber?, expirationDate?, notes? }
 */
export const receiveInventory = async (itemData) => {
    const { data } = await API.post('/api/inventory/receive', itemData);
    return data;
};

/**
 * Use/deduct inventory from an item
 * @param {object} useData - { itemId, quantity, requestId? }
 */
export const useInventory = async (useData) => {
    const { data } = await API.post('/api/inventory/use', useData);
    return data;
};

/**
 * Adjust inventory quantity (corrections)
 * @param {object} adjustData - { itemId, newQuantity, notes? }
 */
export const adjustInventory = async (adjustData) => {
    const { data } = await API.post('/api/inventory/adjust', adjustData);
    return data;
};

/**
 * Get usage history for a product (all jobs that used any item of this product)
 * @param {number} productId - Required
 * @param {number} installerId - Required - filters usage to this installer only
 */
export const getProductUsage = async (productId, installerId) => {
    const { data } = await API.get(`/api/inventory/products/${productId}/usage`, { installerId });
    return data.data || data;
};

/**
 * Get usage history for a specific inventory item (barcode/roll)
 * @param {number} itemId - Required - the inventory item ID
 * @param {number} installerId - Required - filters usage to this installer only
 */
export const getItemUsage = async (itemId, installerId) => {
    const { data } = await API.get(`/api/inventory/items/${itemId}/usage`, { installerId });
    return data.data || data;
};

// ============================================================================
// Service Categories (for product categorization)
// ============================================================================

export const getServiceCategories = async () => {
    // Use serviceCategoryConfig endpoint which includes default_unit_id and default_unit_code
    const { data } = await API.get('/serviceCategoryConfig');
    return data.data || data;
};

// Default export for convenience
const InventoryAPI = {
    getUnitsOfMeasure,
    getProducts,
    createProduct,
    updateProduct,
    deleteProduct,
    getStockLevels,
    getLowStockAlerts,
    getInventoryItems,
    lookupBarcode,
    receiveInventory,
    useInventory,
    adjustInventory,
    getProductUsage,
    getItemUsage,
    getServiceCategories
};

export default InventoryAPI;
